import OpenAI from 'openai';
import { DocumentaryScene, DocumentaryStyle } from './documentaryAnalyzer';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';

// Helper to check user subscription
async function getUserSubscription(): Promise<{ isPro: boolean; subscription: string }> {
  const user = auth.currentUser;
  if (!user) return { isPro: false, subscription: 'free' };
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const subscription = userDoc.data()?.subscription || 'free';
    const isPro = ['pro', 'premium', 'enterprise'].includes(subscription);
    return { isPro, subscription };
  } catch {
    return { isPro: false, subscription: 'free' };
  }
}

interface AIDocumentaryAnalysis {
  genre: string;
  style: DocumentaryStyle;
  scenes: DocumentaryScene[];
  narrativeArc: string;
  visualTheme: string;
  keyMoments: string[];
}

export const analyzeDocumentaryWithAIPro = async (
  story: string,
  platform: 'midjourney' | 'veo' | 'universal' = 'midjourney',
  apiKey?: string
): Promise<AIDocumentaryAnalysis> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Please configure your OpenAI API key.');
  }

  const client = new OpenAI({
    apiKey: effectiveApiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are an expert documentary filmmaker and AI prompt engineer. 
  Analyze the given story and create a professional documentary treatment with scene-by-scene visual prompts.
  
  For each scene, provide:
  1. A detailed visual description
  2. Camera work and cinematography
  3. Mood and atmosphere
  4. Specific prompts optimized for ${platform}
  
  Detect the documentary genre (comic, scientific, historical, nature, biographical, investigative, cultural)
  and adapt the visual style accordingly.
  
  Return a structured JSON response with the complete documentary breakdown.`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { 
          role: "user", 
          content: `Create a documentary treatment for this story: "${story}"
          
          Return as JSON with this structure:
          {
            "genre": "detected genre",
            "visualTheme": "overall visual theme description",
            "narrativeArc": "story structure analysis",
            "keyMoments": ["list of key dramatic moments"],
            "scenes": [
              {
                "sceneNumber": 1,
                "title": "Scene title",
                "description": "What happens",
                "duration": "X seconds",
                "imagePrompt": "Complete prompt for image generation",
                "videoPrompt": "Complete prompt for video generation",
                "cameraWork": "Camera technique",
                "mood": "Emotional tone",
                "soundscape": "Audio description"
              }
            ]
          }`
        }
      ],
      temperature: 0.7,
      max_tokens: 2000
    });

    const response = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Map to our structure
    const analysis: AIDocumentaryAnalysis = {
      genre: response.genre || 'universal',
      style: mapGenreToStyle(response.genre),
      scenes: enhanceScenePrompts(response.scenes || [], platform, response.genre),
      narrativeArc: response.narrativeArc || '',
      visualTheme: response.visualTheme || '',
      keyMoments: response.keyMoments || []
    };

    return analysis;
  } catch (error) {
    console.error('Error analyzing documentary with AI:', error);
    throw error;
  }
};

// Helper function to map genre to style
function mapGenreToStyle(genre: string): DocumentaryStyle {
  const styleMap: Record<string, DocumentaryStyle> = {
    'comic': {
      name: 'Comic Documentary',
      basePrompt: 'humorous documentary style, candid moments, comedic timing',
      lighting: 'bright, natural lighting',
      cameraWork: 'handheld camera, quick zooms, reaction shots',
      mood: 'lighthearted, funny, entertaining',
      colorGrading: 'vibrant, saturated colors',
      soundscape: 'upbeat music, comedic sound effects, laugh tracks'
    },
    'scientific': {
      name: 'Scientific Documentary',
      basePrompt: 'scientific visualization, data-driven imagery, research footage',
      lighting: 'clinical lighting, laboratory conditions',
      cameraWork: 'steady shots, macro lenses, time-lapse',
      mood: 'analytical, precise, educational',
      colorGrading: 'cool tones, high contrast',
      soundscape: 'ambient electronic, technical sounds'
    },
    'historical': {
      name: 'Historical Documentary',
      basePrompt: 'historical reenactment, period-accurate details, archival style',
      lighting: 'period-appropriate lighting, sepia tones',
      cameraWork: 'steady establishing shots, slow pans',
      mood: 'nostalgic, reverent, educational',
      colorGrading: 'desaturated, film grain, vintage look',
      soundscape: 'orchestral score, period music'
    },
    'nature': {
      name: 'Nature Documentary',
      basePrompt: 'wildlife cinematography, natural habitats, animal behavior',
      lighting: 'golden hour, natural sunlight',
      cameraWork: 'telephoto lenses, drone shots, macro photography',
      mood: 'awe-inspiring, peaceful, majestic',
      colorGrading: 'natural colors, high dynamic range',
      soundscape: 'nature sounds, ambient music'
    },
    'biographical': {
      name: 'Biographical Documentary',
      basePrompt: 'intimate portrait, personal story, character study',
      lighting: 'soft, intimate lighting',
      cameraWork: 'close-ups, interview setups, handheld moments',
      mood: 'personal, emotional, inspiring',
      colorGrading: 'warm tones, soft contrast',
      soundscape: 'emotional score, personal narratives'
    },
    'investigative': {
      name: 'Investigative Documentary',
      basePrompt: 'investigative journalism, uncovering truth, evidence-based',
      lighting: 'dramatic shadows, noir-inspired',
      cameraWork: 'handheld urgency, hidden cameras, dramatic reveals',
      mood: 'tense, mysterious, revealing',
      colorGrading: 'high contrast, desaturated',
      soundscape: 'suspenseful music, dramatic stings'
    },
    'cultural': {
      name: 'Cultural Documentary',
      basePrompt: 'cultural exploration, traditions, human stories',
      lighting: 'natural, authentic lighting',
      cameraWork: 'observational style, wide establishing shots',
      mood: 'respectful, immersive, educational',
      colorGrading: 'rich, culturally appropriate colors',
      soundscape: 'traditional music, ambient sounds'
    }
  };

  return styleMap[genre.toLowerCase()] || styleMap['cultural'];
}

// Helper function to enhance scene prompts
function enhanceScenePrompts(scenes: any[], platform: string, genre: string): DocumentaryScene[] {
  return scenes.map((scene, index) => {
    const platformParams = getPlatformParameters(platform);
    
    return {
      sceneNumber: scene.sceneNumber || index + 1,
      title: scene.title || `Scene ${index + 1}`,
      description: scene.description || '',
      duration: scene.duration || '5-10 seconds',
      imagePrompt: `${scene.imagePrompt || ''} ${platformParams.image}`,
      videoPrompt: `${scene.videoPrompt || ''} ${platformParams.video}`,
      cameraWork: scene.cameraWork || 'steady shot',
      mood: scene.mood || 'neutral',
      soundscape: scene.soundscape || 'ambient'
    };
  });
}

// Helper function to get platform parameters
function getPlatformParameters(platform: string) {
  const params: Record<string, { image: string; video: string }> = {
    'midjourney': {
      image: '--ar 16:9 --v 6 --style raw --q 2',
      video: '--video --motion 2'
    },
    'veo': {
      image: 'ultra-high definition, 8K resolution, cinematic',
      video: 'smooth motion, 60fps, professional cinematography'
    },
    'universal': {
      image: 'high quality, professional',
      video: 'smooth motion, cinematic'
    }
  };

  return params[platform] || params['universal'];
}

export const generateStoryFromPromptPro = async (
  prompt: string,
  genre?: string,
  apiKey?: string
): Promise<string> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Please configure your OpenAI API key.');
  }

  const client = new OpenAI({
    apiKey: effectiveApiKey,
    dangerouslyAllowBrowser: true
  });

  const genreContext = genre ? ` in a ${genre} documentary style` : '';
  
  const systemPrompt = `You are a creative documentary storyteller. 
  Transform the given prompt into a compelling documentary narrative${genreContext}.
  Create a story with clear beginning, middle, and end.
  Include specific visual moments and emotional beats.
  Make it suitable for a 2-3 minute documentary.`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { 
          role: "user", 
          content: `Create a documentary story from this prompt: "${prompt}"`
        }
      ],
      temperature: 0.8,
      max_tokens: 500
    });

    return completion.choices[0].message.content || prompt;
  } catch (error) {
    console.error('Error generating story:', error);
    throw error;
  }
};

export const optimizePromptForPlatformPro = async (
  prompt: string,
  platform: string,
  apiKey?: string
): Promise<string> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Please configure your OpenAI API key.');
  }

  const client = new OpenAI({
    apiKey: effectiveApiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are an expert at optimizing prompts for ${platform}.
  Enhance the given prompt with platform-specific parameters and style modifiers.
  Make it more detailed and effective for best results on ${platform}.`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { 
          role: "user", 
          content: `Optimize this prompt for ${platform}: "${prompt}"`
        }
      ],
      temperature: 0.6,
      max_tokens: 300
    });

    return completion.choices[0].message.content || prompt;
  } catch (error) {
    console.error('Error optimizing prompt:', error);
    return prompt; // Return original on error
  }
};